import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("ccfi_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  // Validate the stored token on mount (skip entirely if there's no token).
  useEffect(() => {
    if (!localStorage.getItem("ccfi_token")) {
      setUser(null);
      return;
    }
    let alive = true;
    api.get("/auth/me")
      .then((r) => {
        if (!alive) return;
        setUser(r.data);
        localStorage.setItem("ccfi_user", JSON.stringify(r.data));
      })
      .catch(() => {
        if (!alive) return;
        setUser(null);
        localStorage.removeItem("ccfi_user");
        localStorage.removeItem("ccfi_token");
      });
    return () => { alive = false; };
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("ccfi_token", data.token);
      localStorage.setItem("ccfi_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const setupPassword = async (token, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup-password", { token, password });
      if (data.token) localStorage.setItem("ccfi_token", data.token);
      localStorage.setItem("ccfi_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const register = async (email, password, name) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      // Only the first-user bootstrap returns a session; admin-created accounts are pending.
      if (data.user && data.token) {
        localStorage.setItem("ccfi_token", data.token);
        localStorage.setItem("ccfi_user", JSON.stringify(data.user));
        setUser(data.user);
      }
      return data;
    } finally { setLoading(false); }
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    if (data.token) localStorage.setItem("ccfi_token", data.token);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("ccfi_token");
    localStorage.removeItem("ccfi_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, changePassword, setupPassword, loading }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Everyone lands on their personal, self-built dashboard.
export function defaultRoute(user) {
  if (!user) return "/login";
  return "/dashboard";
}
